import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import { useNavigate } from "react-router-dom";
import { useWallet } from "../../context/walletContext";
import { logout } from "../../features/authSlice";
import { showToastMessage } from "../../utils/toastUtils";
import {
  toggleAdminSidebar,
  toggleAdminSidebarCollapsed,
} from "../../app/adminSidebarSlice";
import { toMediaUrl } from "../../utils/mediaUrl";
import { logoutMerchantUser } from "../../api/userAuth";
import ThemeToggle from "../../components/ThemeToggle";
import ConfirmDialog from "../../components/ui/ConfirmDialog";
import { formatCompactINR } from "../../utils/validators";
import BrandLogo from "../../utils/logoImg";
import { DESIGN_TOKENS } from "../../designSystem/tokens";

const NAV_H = DESIGN_TOKENS.layout.navbarHeight;

const NavbarAdmin = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const { adminWallet } = useWallet();
  const { userInfo } = useSelector((state) => state?.auth);
  const { collapsed, isOpen } = useSelector((state) => state.adminSidebar);
  const [anchorEl, setAnchorEl] = useState(null);
  const [logoutOpen, setLogoutOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const menuOpen = Boolean(anchorEl);

  const credit =
    adminWallet?.credit?.operativeBalance ??
    adminWallet?.credit?.balance ??
    adminWallet?.credit?.wallet ??
    0;
  const debit =
    adminWallet?.debit?.operativeBalance ??
    adminWallet?.debit?.cdwallet ??
    adminWallet?.debit?.balance ??
    0;
  const balance = Number(credit);

  const avatarSrc = userInfo?.logo
    ? toMediaUrl(userInfo.logo)
    : undefined;

  const handleLogout = async () => {
    setLoggingOut(true);
    try {
      await logoutMerchantUser();
    } catch {
      /* still clear local session */
    } finally {
      dispatch(logout());
      setLoggingOut(false);
      setLogoutOpen(false);
      showToastMessage("success", "Logged out successfully");
      navigate("/", { replace: true });
    }
  };

  const handleSidebarToggle = () => {
    if (isDesktop) dispatch(toggleAdminSidebarCollapsed());
    else dispatch(toggleAdminSidebar());
  };

  const showBrandInHeader = !isDesktop || collapsed;
  const toggleLabel = isDesktop
    ? collapsed
      ? "Show sidebar"
      : "Hide sidebar"
    : isOpen
    ? "Close menu"
    : "Open menu";

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        color="inherit"
        sx={{
          bgcolor: "var(--surface-color)",
          color: "var(--text-dark)",
          borderBottom: "1px solid var(--border-color)",
          zIndex: 30,
          height: NAV_H,
          justifyContent: "center",
        }}
      >
        <Toolbar
          disableGutters
          sx={{
            minHeight: `${NAV_H}px !important`,
            height: NAV_H,
            px: { xs: 3.5, sm: 2 },
            gap: 1,
          }}
        >
          <Tooltip title={toggleLabel}>
            <IconButton
              edge="start"
              onClick={handleSidebarToggle}
              aria-label={toggleLabel}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "var(--radius-md)",
                width: 36,
                height: 36,
              }}
            >
              {isDesktop ? (
                collapsed ? (
                  <MenuIcon fontSize="small" />
                ) : (
                  <MenuOpenIcon fontSize="small" />
                )
              ) : (
                <MenuIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>

          {showBrandInHeader && (
            <Box sx={{ display: "flex", alignItems: "center", ml: 0.5 }}>
              <BrandLogo
                variant={theme.palette.mode === "dark" ? "white" : "blue"}
                size="sm"
              />
            </Box>
          )}

          <Box sx={{ flex: 1 }} />

          <Chip
            size="small"
            label={` ${formatCompactINR(balance)}`}
            sx={{
              display: { xs: "none", sm: "inline-flex" },
              fontWeight: 600,
              bgcolor: "action.hover",
              borderRadius: "var(--radius-sm)",
              height: 32,
            }}
          />
          <Chip
            size="small"
            color="success"
            variant="outlined"
            label={`Cr ${formatCompactINR(credit)}`}
            sx={{
              display: { xs: "none", md: "inline-flex" },
              fontWeight: 600,
              borderRadius: "var(--radius-sm)",
              height: 32,
            }}
          />
          <Chip
            size="small"
            color="error"
            variant="outlined"
            label={`Dr ${formatCompactINR(debit)}`}
            sx={{
              display: { xs: "none", md: "inline-flex" },
              fontWeight: 600,
              borderRadius: "var(--radius-sm)",
              height: 32,
            }}
          />

          <ThemeToggle size="small" />

          <IconButton
            aria-label="Notifications"
            size="small"
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "var(--radius-md)",
              width: 36,
              height: 36,
            }}
            onClick={() => navigate("/admin/notification")}
          >
            <NotificationsNoneOutlinedIcon fontSize="small" />
          </IconButton>

          <Box
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              cursor: "pointer",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: "var(--radius-md)",
              pl: 0.5,
              pr: { xs: 0.5, sm: 1 },
              py: 0.35,
              minHeight: 36,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Avatar
              src={avatarSrc}
              alt={userInfo?.name || "Admin"}
              sx={{ width: 28, height: 28, fontSize: 13 }}
            >
              {(userInfo?.name || "A").charAt(0)}
            </Avatar>
            <Box sx={{ display: { xs: "none", md: "block" }, minWidth: 0, maxWidth: 140 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, lineHeight: 1.15, fontSize: 13 }}
                noWrap
              >
                {userInfo?.name || "Admin"}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ lineHeight: 1.1, fontSize: 11 }}
                noWrap
              >
                {userInfo?.userName || "—"}
              </Typography>
            </Box>
          </Box>

          <Menu
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={() => setAnchorEl(null)}
            disableScrollLock
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ zIndex: 2000 }}
            PaperProps={{
              sx: {
                minWidth: 200,
                mt: 1,
                zIndex: 2000,
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--border-color)",
                boxShadow: "var(--shadow-sm)",
              },
            }}
          >
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                navigate("/admin/profile");
              }}
            >
              <PersonOutlineOutlinedIcon fontSize="small" sx={{ mr: 1.25 }} />
              Profile
            </MenuItem>
            {userInfo?.role === "super_admin" && (
              <MenuItem
                component="a"
                href="https://ezypayind.com"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setAnchorEl(null)}
              >
                <OpenInNewOutlinedIcon fontSize="small" sx={{ mr: 1.25 }} />
                Merchant Portal
              </MenuItem>
            )}
            <Divider />
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
                setLogoutOpen(true);
              }}
              sx={{ color: "error.main" }}
            >
              <LogoutOutlinedIcon fontSize="small" sx={{ mr: 1.25 }} />
              Logout
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <ConfirmDialog
        open={logoutOpen}
        title="Sign out"
        message="Are you sure you want to logout from the admin panel?"
        confirmLabel="Logout"
        confirmColor="error"
        loading={loggingOut}
        onCancel={() => setLogoutOpen(false)}
        onConfirm={handleLogout}
      />
    </>
  );
};

export default NavbarAdmin;
